import { EpicRefined } from 'interfaces/middleware'
import {
  VideoCallAction,
  AgoraPublishLocalStream,
  AgoraUnPublishLocalStream,
  AgoraAudioInputAction,
  AgoraVideoInputAction,
  SetMediaPermissionsAction,
} from 'components/features/videoCalls/types/actions'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { mergeMap, catchError } from 'rxjs/operators'
import { of, EMPTY, merge } from 'rxjs'
import { CreateStreamProps, createAgoraStream, fromStreamEvents } from 'components/features/videoCalls/agoraHelpers'
import { mergeMapAsync } from 'rxOperators/mergeMapAsync'
import { getPermissions, filterAgoraClientEvent } from 'components/features/videoCalls/helpers'
import { agoraInstances } from 'components/features/videoCalls/agoraInstances'
import { byType } from 'rxOperators/byType'
import { Stream } from 'agora-rtc-sdk'

const NOT_PERMITTED_ERROR = 'NOT_PERMITTED_ERROR' as const

export const publishLocalStreamEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  filterRequestOfType(AgoraPublishLocalStream.type, 'request'),
  mergeMap((action) => of(null).pipe(
    mergeMapAsync(async () => {
      const permissions = await getPermissions()
      const { videoCall } = store.getState().app
      const { clientId, state } = videoCall.client
      const { videoMuteState, audioMuteState, streamId, audioInputDeviceId, videoInputDeviceId } = videoCall.localStream

      const streamProps: CreateStreamProps = {
        video: permissions.cameraPermissions !== 'denied',
        audio: permissions.microphonePermissions !== 'denied',
        ...audioInputDeviceId ? { microphoneId: audioInputDeviceId } : {},
        ...videoInputDeviceId ? { cameraId: videoInputDeviceId } : {},
      }

      const client = agoraInstances.clients.get(clientId as any)
      if (!client || state !== 'active' || !streamId) {
        throw new Error()
      }

      let stream: Stream | null = null
      try {
        stream = await createAgoraStream(streamId, streamProps)
        stream.setVideoProfile('720p_6')
      } catch (e) {}
      if (!stream) {
        throw {
          type: NOT_PERMITTED_ERROR,
          streamProps,
        }
      }

      return {
        stream,
        streamId,
        client,
        isVideoMute: videoMuteState === 'mute',
        isAudioMute: audioMuteState === 'mute',
      }
    }),
    mergeMapAsync(async (event) => {
      const { stream, client } = event
      await new Promise<void>((res, rej) => {
        client.publish(stream, rej)
        res()
      })

      return event
    }),
    mergeMap(({ stream, streamId, isVideoMute, isAudioMute }) => {
      const videoTrack: MediaStreamTrack | undefined = stream.getVideoTrack() as any
      const videoInputDeviceId = videoTrack?.getCapabilities().deviceId ?? null

      const audioTrack: MediaStreamTrack | undefined = stream.getAudioTrack() as any
      const audioInputDeviceId = audioTrack?.getCapabilities().deviceId ?? null

      agoraInstances.streams.set(streamId, stream)

      return merge(
        fromStreamEvents(stream),
        of(action.success({
          streamId: +stream.getId(),
          videoInputDeviceId,
          audioInputDeviceId,
          isVideoMute,
          isAudioMute,
        }))
      )
    }),
    catchError((error) => {
      if (error && error.type === NOT_PERMITTED_ERROR) {
        return merge(
          of(new SetMediaPermissionsAction({
            ...error?.streamProps?.video ? { cam: 'denied' } : {},
            ...error?.streamProps?.audio ? { mic: 'denied' } : {},
          })),
          of(action.fail()),
        )
      }
      return of(action.fail())
    })
  )),
)

export const unpublishLocalStreamEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  filterRequestOfType(AgoraUnPublishLocalStream.type, 'request'),
  mergeMap((action) => of(null).pipe(
    mergeMapAsync(async () => {
      const { videoCall } = store.getState().app
      const { streamId } = videoCall.localStream
      const stream = agoraInstances.streams.get(streamId as any)
      if (!stream || !streamId) {
        throw new Error()
      }

      stream.close()
      return action.success({ streamId })
    }),
    catchError(() => of(action.fail()))
  )),
)


export const switchDeviceEpic: EpicRefined<VideoCallAction> = (action$, store) => merge(
  action$.pipe(
    byType(AgoraVideoInputAction.type)
  ),
  action$.pipe(
    byType(AgoraAudioInputAction.type)
  ),
).pipe(
  mergeMap((action) => {
    const { streamId } = store.getState().app.videoCall.localStream
    const stream = agoraInstances.streams.get(streamId as any)
    if (!stream || !action.payload) {
      return EMPTY
    }

    const kind = action.type === AgoraAudioInputAction.type ? 'audio' : 'video'
    stream.switchDevice(kind, action.payload)

    return EMPTY
  })
)


export const recordingDeviceChangedEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  filterAgoraClientEvent('recording-device-changed'),
  mergeMap((event) => {
    if (event.state !== 'INACTIVE') {
      return EMPTY
    }

    const streamId = store.getState().app.videoCall.localStream.streamId
    const stream = agoraInstances.streams.get(streamId as any)
    if (!stream) {
      return EMPTY
    }

    const track = stream.getAudioTrack() as MediaStreamTrack | null
    const deviceId = track?.getCapabilities().deviceId
    if (!deviceId) {
      return EMPTY
    }

    return of(new AgoraAudioInputAction(deviceId))
  })
)

export const publishedEpic: EpicRefined<VideoCallAction> = (action$, store) => action$.pipe(
  filterAgoraClientEvent('stream-published'),
  mergeMap(() => {
    const { streamId, videoMuteState, audioMuteState } = store.getState().app.videoCall.localStream
    const stream = agoraInstances.streams.get(streamId as any)
    if (!stream) {
      return EMPTY
    }

    if (videoMuteState === 'mute') {
      stream.muteVideo()
    } else {
      stream.unmuteVideo()
    }
    if (audioMuteState === 'mute') {
      stream.muteAudio()
    } else {
      stream.unmuteAudio()
    }

    return EMPTY
  }),
)
